import {
  abortGame,
  acceptDraw,
  abandonGame,
  applyMove,
  claimFlag,
  declineDraw,
  joinGame,
  markReadyToStart,
  claimStartForfeit,
  offerDraw,
  resignGame
} from './game.js';
import {
  previousMoveSignature,
  signGameAction,
  verifyGameAction
} from './transcript.js';

function actionId() {
  return crypto.randomUUID();
}

function publicKeyForAction(game, action) {
  if (action.type === 'join') {
    return action.payload?.player?.publicKey?.signingPublicKey;
  }
  if (game.players.white?.playerId === action.playerId) {
    return game.players.white.publicKey?.signingPublicKey;
  }
  if (game.players.black?.playerId === action.playerId) {
    return game.players.black.publicKey?.signingPublicKey;
  }
  return null;
}

export function createSignedGameAction({
  game,
  type,
  payload = {},
  identity,
  now = Date.now()
}) {
  if (!game?.id || !identity?.playerId || !identity?.keyPair?.signing?.secretKey) {
    throw new Error('Game and signing identity are required');
  }

  return signGameAction(
    {
      schemaVersion: 1,
      actionId: actionId(),
      gameId: game.id,
      baseRevision: game.revision,
      type,
      playerId: identity.playerId,
      previousMoveSignature: previousMoveSignature(game),
      payload,
      at: now
    },
    identity.keyPair.signing.secretKey
  );
}

export function createSignedGameSnapshot(game, identity) {
  if (!game?.id || !identity?.playerId || !identity?.keyPair?.signing?.secretKey) {
    throw new Error('Game and signing identity are required');
  }
  return signGameAction(
    {
      schemaVersion: 1,
      signerId: identity.playerId,
      game
    },
    identity.keyPair.signing.secretKey
  );
}

export function verifySignedGameSnapshot(snapshot) {
  const game = snapshot?.game;
  const signer = [game?.players?.white, game?.players?.black]
    .find((player) => player?.playerId === snapshot?.signerId);
  return Boolean(
    signer?.publicKey?.signingPublicKey
    && verifyGameAction(snapshot, signer.publicKey.signingPublicKey)
  );
}

export function applySignedGameAction(game, action) {
  if (!game || action?.gameId !== game.id) {
    throw new Error('Action targets a different game');
  }

  // Ready confirmations are commutative: either player may confirm first, so a
  // peer's ready-start can arrive after a local ready already advanced revision.
  const readyStartOnStartingGame = action.type === 'ready-start' && game.status === 'starting';
  if (action.baseRevision !== game.revision && !readyStartOnStartingGame) {
    throw new Error('Action is based on a stale game revision');
  }

  const signingPublicKey = publicKeyForAction(game, action);
  if (!verifyGameAction(action, signingPublicKey)) {
    throw new Error('Invalid game action signature');
  }

  let next;
  switch (action.type) {
    case 'join':
      next = joinGame(game, action.payload.player, action.at);
      break;
    case 'ready-start':
      next = markReadyToStart(game, action.playerId, action.at);
      if (next === game) {
        return game;
      }
      break;
    case 'claim-start-forfeit':
      next = claimStartForfeit(game, action.playerId, action.at);
      break;
    case 'move':
      if (action.previousMoveSignature !== previousMoveSignature(game)) {
        throw new Error('Move signature chain does not match');
      }
      next = applyMove(
        game,
        {
          playerId: action.playerId,
          from: action.payload.from,
          to: action.payload.to,
          promotion: action.payload.promotion
        },
        action.at
      );
      next = {
        ...next,
        moves: next.moves.map((move, index) =>
          index === next.moves.length - 1
            ? {
              ...move,
              actionId: action.actionId,
              signature: action.signature,
              signedAction: action
            }
            : move
        )
      };
      break;
    case 'resign':
      next = resignGame(game, action.playerId, action.at);
      break;
    case 'offer-draw':
      next = offerDraw(game, action.playerId, action.at);
      break;
    case 'accept-draw':
      next = acceptDraw(game, action.playerId, action.at);
      break;
    case 'decline-draw':
      next = declineDraw(game, action.playerId, action.at);
      break;
    case 'abort':
      next = abortGame(game, action.playerId, action.at);
      break;
    case 'claim-flag':
      next = claimFlag(game, action.playerId, action.at);
      break;
    case 'abandon':
      next = abandonGame(game, action.playerId, action.at);
      break;
    default:
      throw new Error(`Unsupported game action: ${action.type}`);
  }

  return {
    ...next,
    lastActionId: action.actionId
  };
}
